/* eslint-disable no-console */
import React, { createContext, useMemo, useState, FC } from 'react';
import { useMutation, ExecutionResult } from 'react-apollo';

import submitSurveyResponse from './SubmitResponses.graphql';

import {
  GetCurrentSurveySessionQuery,
  SubmitSurveyResponseMutation,
  SubmitSurveyResponseMutationVariables,
  QuestionAnswerInput,
} from '~lib/generatedGqlTypes';
import { QuestionAnswers } from '~app/pages/SurveyTaking/types';

type Survey = GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['survey'];
type Collector = GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['collector'];
type ActiveSurveyPage = GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage'];

type SurveyContextType = {
  survey?: Survey;
  collector?: Collector;
  activePage?: ActiveSurveyPage;
  nextPage(this: void): void;
  previousPage(this: void): void;
  submitSurvey(this: void, answers: QuestionAnswers): Promise<ExecutionResult<SubmitSurveyResponseMutation>>;
};

const DEFAULT_PROMISE_RESOLVER = {
  data: {
    submitSurveyResponse: {
      encryptedSmParam: '',
      success: false,
      userErrors: null,
      respondent: null,
    },
  },
};

export const SurveyContext = createContext<SurveyContextType>({
  survey: undefined,
  collector: undefined,
  activePage: undefined,
  nextPage: () => {},
  previousPage: () => {},
  submitSurvey: async () => {
    return Promise.resolve(DEFAULT_PROMISE_RESOLVER);
  },
});

export type SurveyProviderProps = {
  children: React.ReactNode;
  survey: Survey;
  currentPage: GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage'];
  collector: GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['collector'];
};

const SurveyProvider: FC<SurveyProviderProps> = ({ children, survey: initialSurvey, currentPage, collector }) => {
  // Survey only rendering first page until we have information about who is responsible for providing next page.
  // When we get more information about how we will be acquiring other pages we will add those mechanisms here.
  const activePage = useMemo(() => currentPage, [currentPage]);
  // Survey currently holds error state, setSurvey will need to be called when upstream actions are hooked up.
  const [survey] = useState(initialSurvey);

  const [submitCurrentResponses] = useMutation<SubmitSurveyResponseMutation, SubmitSurveyResponseMutationVariables>(
    submitSurveyResponse
  );

  const nextPage = (): void => {
    console.log('Use this method to get the next page.  Pending decision about pagination');
  };
  const previousPage = (): void => {
    console.log('Use this method to get the previous page.  Pending decision about pagination');
  };

  const submitSurvey = async (answers: QuestionAnswers): Promise<ExecutionResult<SubmitSurveyResponseMutation>> => {
    if (survey) {
      try {
        const responses = Object.keys(answers).map<QuestionAnswerInput>(key => {
          const answer = answers[key];
          const { questionId, value } = answer;

          return {
            questionId,
            pageId: currentPage?.id ?? '',
            openEnded: [{ textAnswer: value, fieldSetId: '' }],
            choice: [],
            implicit: [],
          };
        });

        return await submitCurrentResponses({
          variables: {
            surveyId: survey?.id ?? '',
            input: {
              collectorId: collector?.id ?? '',
              isAnonymous: collector?.isAnonymous ?? false,
              surveyVersion: survey?.version ?? 1,
              dateStarted: new Date().toISOString(),
              respondentId: '',
              responses,
            },
          },
        });
      } catch (error) {
        // To be completed in server error validation story
        console.log(error);
      }
    }
    return Promise.resolve(DEFAULT_PROMISE_RESOLVER);
  };

  return (
    <SurveyContext.Provider
      value={{
        survey,
        collector,
        activePage,
        nextPage,
        previousPage,
        submitSurvey,
      }}
    >
      <>{children}</>
    </SurveyContext.Provider>
  );
};

export default SurveyProvider;
